import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from 'src/utils/lni18n';
import { Card, CardBody, CardHeader } from 'reactstrap';

import courseService from 'src/course/CourseService';
import { useCallback } from 'react';
import { CourseEditResponse, TinyTheme } from 'src/course/domain/CourseEditModel';
import classes from "../SideBar.module.scss";

import LnIcon from 'src/components/LnIcon';
import ThemeCard from './ThemeCard';

import { DndContext, DragOverlay, defaultDropAnimationSideEffects } from "@dnd-kit/core";
import type { DropAnimation, UniqueIdentifier } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { createPortal } from 'react-dom';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import PartCard from './PartCard';
import { urlService } from 'src/services/NavService';
import coursePartService from 'src/coursepart/CoursePartService';
import { useThemeDragNDrop } from 'src/course/Theme/UseThemeDragNDrop';
import { useAddTheme } from 'src/course/UseAddTheme';
import { useConfirm } from 'src/components/Modalservice/UseConfirm';

const dummyTheme: TinyTheme = {
    BehaviourType: 0,
    Id: "string",
    Name: "string",
    Order: 0,
    NewID: null,
    Parts: []
}

export default function CoursesTree(props: { course: CourseEditResponse }) {
    const { languageService: t } = useI18n();
    const navigate = useNavigate();
    const { themeId: currentThemeId, partId, courseId } = useParams();
    const currentCourseId = props.course.Course.Id;
    const location = useLocation();
    const confirm = useConfirm();

    const addTheme = useAddTheme();

    const selectedItem = useCallback((code: string) => {

        if (!courseId) return "";

        if (!currentThemeId) {
            return location.pathname.indexOf(code) > -1 ? "selected" : "";
        }

        if (currentThemeId) {
            if (!partId) {
                return code === currentThemeId ? "selected" : "";
            }
            else {
                return code === partId ? "selected" : "";
            }
        }
    }, [currentThemeId, partId, location, courseId]);

    const inAContainer = useCallback((id: UniqueIdentifier) => {
        return !!props.course.Course.Themes.find(theme => theme.Id === id);
    }, [props.course.Course.Themes]);

    const allThemes = "allThemes";

    const { sensors, dragOver, dragEnd, active, setActive } = useThemeDragNDrop(currentCourseId, allThemes);


    const dropAnimation: DropAnimation = {
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };


    const getThemeById = useCallback((id: UniqueIdentifier) => {
        return props.course.Course.Themes?.find(th => th.Id === id)!;
    }, [props.course.Course.Themes]);

    const getPartById = useCallback((id: UniqueIdentifier) => {
        let part: any = undefined;
        props.course.Course.Themes?.forEach(th => {
            const p = th.Parts.find(p => p.Id === id);
            if (p) part = p;
        });

        return part;

    }, [props.course.Course.Themes]);


    async function closeCourse(course: CourseEditResponse) {

        if (course.Locked || !course?.IsDirty) {

            if (partId && currentThemeId) {
                const result = await coursePartService.checkIfPartCanClose(partId);
                if (result) {
                    navigate(urlService.urlToPartInCourse(currentCourseId, currentThemeId, partId, "publish"));
                }
            }
            else {
                doCloseCourse(course.Course.Id);
                return;
            }
        }

        confirm("md", `${t.getText("course")}: ${course.Course.Name}`,
            <div className="preserve-white" >{t.getText("publish.before.close")}</div>)
            .then(async (res: boolean) => {
                if (res) {
                    navigate(urlService.urlToCourse(courseId!, "publish"));
                }
                else {
                    doCloseCourse(course.Course.Id);
                }
            }).catch((e: Error) => { /* OK never really happens */ });


    }

    async function doCloseCourse(courseId: string) {
        const nextCourseId = await courseService.closeCourse(courseId);

        window.setTimeout(() => {
            if (nextCourseId) {
                navigate(urlService.urlToCourse(nextCourseId));
            }
            else {
                navigate(`/`);
            }
        }, 100);
    }


    const navigateToCourseFeature = async (feature: string) => {
        navigate(urlService.urlToCourse(currentCourseId, feature));
        return;
    };

    const readOnly = !!props.course.Locked;

    return (
        <div className='mb-3'>
            <Card className={[classes.theme, "border border-primary"].join(' ')}>
                <CardHeader className={`${classes.header} d-flex pr-1 align-items-center`}>
                    <LnIcon name="eye" className='mr-3' />
                    <strong>{props.course.Course.Name}</strong>
                    {readOnly && <LnIcon name="locked" className='icon-small ml-3 mt-1' />}
                    <div className="ml-auto">
                        {props.course.IsDirty && <LnIcon name="alert" className='icon-small mb-2 mr-2' />}

                    </div>



                </CardHeader>
                <CardBody className={classes.parts}>

                    <div onClick={() => navigateToCourseFeature("general")} className={[selectedItem("general"), classes.part, "w-100"].join(' ')}>
                        <div className={classes.partname}>{t.getText("general.course")}</div>
                    </div>

                    <div onClick={() => navigateToCourseFeature("publish")} className={[selectedItem("publish"), classes.part, "w-100"].join(' ')}>
                        <div className={classes.partname}>{t.getText("publish")}</div>
                    </div>

                    {!readOnly && <>
                        <div onClick={() => navigateToCourseFeature("addons")} className={[selectedItem("addons"), classes.part, "w-100"].join(' ')}>
                            <div className={classes.partname}>{t.getText("courseaddons")}</div>
                        </div>

                        <div onClick={() => navigateToCourseFeature("diploma")} className={[selectedItem("diploma"), classes.part, "w-100"].join(' ')}>
                            <div className={classes.partname}>{t.getText("diploma")}</div>
                        </div>
                    </>
                    }
                    
                    <div onClick={() => navigateToCourseFeature("print")} className={[selectedItem("print"), classes.part, "w-100"].join(' ')}>
                        <div className={classes.partname}>{t.getText("print")}</div>
                    </div>

                    <div className={[classes.part, "mt-3 w-100"].join(' ')}>
                        <button onClick={(e) => closeCourse(props.course)} className="btn btn-link" type="button">
                            <div className={classes.partname}>{t.getText("close")}</div>
                        </button>
                    </div>



                </CardBody>
            </Card >

            {!readOnly && courseId &&
                <button onClick={() => addTheme(courseId)} className="ml-auto  mb-5 btn btn-primary">{t.getText("add.theme")}</button>
            }

            <DndContext sensors={sensors}

                onDragStart={({ active }) => { setActive(active); }}
                onDragOver={dragOver} onDragEnd={dragEnd} onDragCancel={() => { setActive(null); }}>

                <div role="list" className='position-relative'>
                    <SortableContext items={props.course.Course.Themes.map(l => l.Id)} id={allThemes}>
                        {props.course.Course.Themes.map(t => <ThemeCard readOnly={readOnly} key={t.Id}
                            currentThemeId={currentThemeId}
                            currentCourseId={currentCourseId} theme={t} currentPartId={partId} />)}
                    </SortableContext>
                </div>

                {createPortal(
                    <DragOverlay adjustScale={false} dropAnimation={dropAnimation} modifiers={[restrictToVerticalAxis]} >

                        {active
                            ? inAContainer(active.id)
                                ? <ThemeCard readOnly={true} currentThemeId={currentThemeId} dragged={true}
                                    currentCourseId={currentCourseId}
                                    theme={getThemeById(active.id)} currentPartId={partId} />

                                : <PartCard readOnly={true} dragged={true} part={getPartById(active.id)} partId={partId}
                                    theme={dummyTheme} currentCourseId={currentCourseId} />
                            : null}
                    </DragOverlay>,
                    document.body
                )}


            </DndContext>

        </div>
    )
}
